<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text :class="{ 'px-0': $vuetify.breakpoint.mobile }">
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-tabs v-model="selectedTab" :show-arrows="$vuetify.breakpoint.mobile">
          <v-tab
            v-for="(tab, i) in tabs"
            v-show="isShowTab(tab)"
            :key="i"
            :class="{
              'justify-start': $vuetify.breakpoint.xlOnly,
            }"
          >
            {{ tab.title }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedTab">
          <v-tab-item v-for="(tab, i) in tabs" v-show="isShowTab(tab)" :key="i">
            <v-card v-if="tab.id === 'info'" flat>
              <!-- <info-sub-form /> -->
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" md="2">
                    <div class="label mb-2">Class Type</div>
                    <v-select
                      v-model="form.class_type"
                      :items="classTypeOptions"
                      :disabled="mode !== 'add'"
                      :rules="[validationRules.required]"
                      :error="form.errors.class_type"
                      :error-messages="form.errors.class_type"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Choose class type"
                      @change="changeClassType"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="d-flex justify-space-between align-center">
                      <div class="label mb-2">Subscription Package</div>
                      <div v-if="!!form.class_type">
                        <v-chip color="warning" small dark>
                          <strong>{{
                            classroomTypeProps(form.class_type, 'text')
                          }}</strong>
                        </v-chip>
                      </div>
                    </div>
                    <v-autocomplete
                      v-model="form.subscription_package_id"
                      :loading="isSubscriptionPackageListLoading"
                      :items="subscriptionPackages"
                      :item-text="subscriptionPackageItemText"
                      :rules="[validationRules.required]"
                      :error="form.errors.subscription_package_id"
                      :error-messages="form.errors.subscription_package_id"
                      :disabled="isSubscriptionPackageDisabled"
                      item-value="id"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Choose subscription package"
                      @change="changeSubscriptionPackage($event)"
                    />
                  </v-col>
                  <v-col v-if="isPrivate" cols="12" md="3">
                    <div class="label mb-2">Student (Private Class)</div>
                    <v-autocomplete
                      v-model="form.private_student_id"
                      :items="students"
                      item-text="name"
                      item-value="id"
                      :loading="isStudentListLoading"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Choose student"
                      @change="changePrivateStudent($event)"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="label mb-2">Teacher</div>
                    <v-autocomplete
                      v-model="form.teacher_id"
                      :items="teachers"
                      :item-text="teacherItemText"
                      item-value="id"
                      :rules="[validationRules.required]"
                      :loading="isTeacherListLoading"
                      :error="form.errors.teacher_id"
                      :error-messages="form.errors.teacher_id"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Choose teacher"
                    >
                      <template #prepend-item>
                        <v-container fluid @mousedown.prevent>
                          <v-switch
                            v-model="isNativeTeacher"
                            inset
                            dense
                            hide-details
                            label="Native speaker?"
                          />
                        </v-container>
                        <v-divider></v-divider>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col md="6" cols="12">
                    <div class="label mb-2">Class Name</div>
                    <v-text-field
                      v-model="form.name"
                      :error="form.errors.name"
                      :error-messages="form.errors.name"
                      :rules="[validationRules.required]"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Class name"
                    />
                  </v-col>
                  <v-col md="6" cols="12">
                    <div class="label mb-2">Class Name (Chinese)</div>
                    <v-text-field
                      v-model="form.name_cn"
                      :error="form.errors.name_cn"
                      :error-messages="form.errors.name_cn"
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Class name (Chinese)"
                    />
                  </v-col>
                </v-row>
                <div class="label mb-2">Online Class URL</div>
                <v-text-field
                  v-model="form.online_class_url"
                  solo
                  flat
                  outlined
                  dense
                  placeholder="Online Class URL"
                />
                <div class="label mb-2">Status</div>
                <v-select
                  v-model="form.status"
                  :items="classStatusOptions"
                  :error="form.errors.status"
                  :error-messages="form.errors.status"
                  solo
                  flat
                  outlined
                  dense
                  placeholder="Choose class status"
                />
              </v-card-text>
            </v-card>
            <v-card v-if="tab.id === 'students'" flat>
              <v-card-text>
                <v-list
                  v-if="students.length > 0"
                  flat
                  subheader
                  class="mb-4"
                  two-line
                >
                  <v-checkbox
                    v-model="isAllStudentsSelected"
                    label="Choose all"
                    :indeterminate="isSomeStudentsSelected"
                    :disabled="students.length === 0"
                  />
                  <template v-for="(student, j) in students">
                    <v-list-item :key="j">
                      <v-list-item-icon>
                        <v-checkbox
                          v-model="form.student_ids"
                          :value="student.id"
                        />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{
                          student.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ student.email }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card-text>
            </v-card>
            <v-card v-if="tab.id === 'period'" flat>
              <v-card-title> Class Period </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row v-if="!!selectedSubscriptionPackage" align="center">
                  <v-col
                    cols="12"
                    md="6"
                    class="d-flex align-center justify-space-between"
                  >
                    <h3>
                      Lesson Plan:
                      {{
                        bilingualText(
                          selectedSubscriptionPackage.lesson_plan,
                          'name',
                          'name_cn'
                        )
                      }}
                    </h3>
                    <v-divider vertical></v-divider>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row align="center">
                      <v-col cols="12" md="4" class="label">
                        Total Term:
                        {{
                          selectedSubscriptionPackage.lesson_plan.total_terms
                        }}
                      </v-col>
                      <v-col cols="12" md="4" class="label">
                        Total Month:
                        {{
                          selectedSubscriptionPackage.lesson_plan.total_months
                        }}
                      </v-col>
                      <v-col cols="12" md="4" class="label">
                        Total Session:
                        {{
                          selectedSubscriptionPackage.lesson_plan.total_sessions
                        }}
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="12" md="6">
                    <v-row align="center">
                      <v-col>
                        <div class="label mb-2">Start Date</div>
                        <v-menu
                          ref="startDateMenu"
                          v-model="startDateMenu"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template #activator="{ on, attrs }">
                            <v-text-field
                              v-model="form.start_date"
                              :rules="[validationRules.required]"
                              :error="form.errors.start_date"
                              :error-message="form.errors.start_date"
                              placeholder="Select class start date"
                              prepend-icon="mdi-calendar"
                              solo
                              flat
                              outlined
                              dense
                              readonly
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form.start_date"
                            :max="form.end_date"
                            @change="
                              $refs.startDateMenu[0].save(form.start_date)
                            "
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col>
                        <div class="label mb-2">End Date</div>
                        <v-menu
                          ref="endDateMenu"
                          v-model="endDateMenu"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template #activator="{ on, attrs }">
                            <v-text-field
                              v-model="form.end_date"
                              :rules="[validationRules.required]"
                              :error="form.errors.end_date"
                              :error-message="form.errors.end_date"
                              placeholder="Select class end date"
                              prepend-icon="mdi-calendar"
                              solo
                              flat
                              outlined
                              dense
                              readonly
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form.end_date"
                            :min="form.start_date"
                            @change="$refs.endDateMenu[0].save(form.end_date)"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-row align="center">
                      <v-col cols="12" md="4">
                        <div class="label mb-2">Start Term</div>
                        <v-text-field
                          v-model="form.start_term"
                          type="number"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Start term"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="label mb-2">Start Month</div>
                        <v-text-field
                          v-model="form.start_month"
                          type="number"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Start month"
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <div class="label mb-2">Start Session</div>
                        <v-text-field
                          v-model="form.start_session"
                          type="number"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Start session"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col v-if="mode === 'edit'" cols="12" md="4">
                    <v-btn
                      depressed
                      color="warning"
                      @click="extendScheduleDialog = true"
                    >
                      Extend Class
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-title>Weekly Session</v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-alert
                  v-if="selectedSubscriptionPackage"
                  type="info"
                  text
                  dense
                >
                  <strong>
                    {{ selectedSubscriptionPackage.lesson_plan.name }}
                    {{
                      '(' +
                      classroomTypeProps(
                        selectedSubscriptionPackage.classroom_type,
                        'text'
                      ) +
                      ')'
                    }}
                  </strong>
                </v-alert>
                <v-row
                  v-for="(weeklySchedule, j) in form.weekly_schedules"
                  :key="j"
                  align="center"
                >
                  <v-col cols="12" md="2">
                    <h3>Session {{ j + 1 }}</h3>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-select
                      v-model="weeklySchedule.day_of_week"
                      :items="dayOfWeekOptions"
                      hide-details
                      solo
                      flat
                      outlined
                      dense
                      placeholder="Choose weekday"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <time-picker
                      v-model="weeklySchedule.start_time"
                      input-width="72px"
                      close-on-complete
                      manual-input
                      class="vue__time-picker--dense"
                    />
                    <div class="mx-2">-</div>
                    <time-picker
                      v-model="weeklySchedule.end_time"
                      input-width="72px"
                      close-on-complete
                      manual-input
                      class="vue__time-picker--dense"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-fade-transition>
                      <p
                        v-if="
                          selectedSubscriptionPackage && !isValidClassPerWeek
                        "
                        class="error--text"
                      >
                        This class only allows
                        {{ selectedSubscriptionPackage.class_per_week }}
                        session(s) per week
                      </p>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <schedule-list
              v-if="mode != 'add' && tab.id === 'schedules'"
              ref="scheduleList"
              :classroom="form"
              :readonly="isReadonly"
              :tab="scheduleTab"
              :show-generate="mode === 'edit'"
              :show-add="mode === 'edit'"
              @generated="onScheduleGenerated"
            ></schedule-list>
            <holiday-list
              v-if="mode != 'add' && tab.id === 'holidays'"
              :classroom-id="classroom.id"
            ></holiday-list>
            <study-report-list
              v-if="mode != 'add' && tab.id === 'studyReports'"
              :classroom-id="classroom.id"
            ></study-report-list>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
    <extend-schedule-dialog
      v-if="extendScheduleDialog"
      v-model="extendScheduleDialog"
      :classroom="classroom"
      @confirm="onSubmitExtendSchedule"
    >
    </extend-schedule-dialog>
  </v-card>
</template>
<script>
import constant from '@/constant'
import { mapActions, mapGetters, mapMutations } from 'vuex'
// import InfoSubForm from '@components/Classroom/InfoSubForm'
import ScheduleList, { TAB_LIST_VIEW } from '@components/Classroom/ScheduleList'
import StudyReportList from '@components/Classroom/StudyReportList'
import HolidayList from '@components/Classroom/HolidayList'
import ExtendScheduleDialog from '@components/Classroom/ExtendScheduleDialog'

export default {
  name: 'ClassroomForm',
  components: {
    // InfoSubForm,
    ScheduleList,
    StudyReportList,
    HolidayList,
    ExtendScheduleDialog,
  },
  provide() {
    return {
      form: this.form,
      mode: this.mode,
      classroom: this.classroom,
      changeClassType: this.changeClassType,
      changeSubscriptionPackage: this.changeSubscriptionPackage,
      changePrivateStudent: this.changePrivateStudent,
    }
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    classroom: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      startDateMenu: false,
      endDateMenu: false,
      isNativeTeacher: false,
      form: this.$inertia.form({
        name: null,
        name_cn: null,
        description: null,
        description_cn: null,
        class_type: null,
        start_date: null,
        end_date: null,
        status: null,
        subscription_package_id: null,
        teacher_id: null,
        // schedules: [],
        weekly_schedules: [],
        student_ids: [],
        private_student_id: null,
        online_class_url: null,
        start_term: null,
        start_month: null,
        start_session: null,
        generate_schedules: false,
      }),
      headers: [
        { text: 'Class Date', value: 'class_date' },
        { text: 'Class Time', value: 'class_time', sortable: false },
        { text: 'Term', value: 'term', sortable: false, align: 'center' },
        { text: 'Month', value: 'month', sortable: false, align: 'center' },
        { text: 'Session', value: 'session', sortable: false, align: 'center' },
        {
          text: 'Lessons',
          value: 'lessons',
          sortable: false,
          class: 'text-center',
        },
        {
          text: 'Action',
          value: 'action',
          sortable: false,
          align: 'center',
        },
      ],
      tabs: [
        {
          title: 'Info',
          id: 'info',
        },
        {
          title: 'Students',
          id: 'students',
        },
        {
          title: 'Period',
          id: 'period',
        },
        {
          title: 'Schedules',
          id: 'schedules',
        },
        {
          title: 'Holidays',
          id: 'holidays',
        },
        {
          title: 'Study Reports',
          id: 'studyReports',
        },
      ],
      selectedTab: 0,
      scheduleTab: TAB_LIST_VIEW,
      extendScheduleDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      classroomDetail: 'classroom/detail',
      lessonPlanList: 'lessonPlan/list',
      isLessonPlanListLoading: 'lessonPlan/isListLoading',
      teacherList: 'teacher/list',
      isTeacherListLoading: 'teacher/isListLoading',
      subscriptionPackageList: 'subscriptionPackage/list',
      isSubscriptionPackageListLoading: 'subscriptionPackage/isListLoading',
      // studentList: 'student/list',
      // isStudentListLoading: 'student/isListLoading',
      studentList: 'classroom/studentList',
      isStudentListLoading: 'classroom/isStudentListLoading',
      lessonList: 'lesson/list',
      isLessonLoading: 'lesson/isListLoading',
      scheduleList: 'classroom/scheduleList',
      isScheduleListLoading: 'classroom/isScheduleListLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    classTypeOptions() {
      return constant.classroomTypeOptions
    },
    classStatusOptions() {
      return constant.classroomStatusOptions
    },
    dayOfWeekOptions() {
      return constant.dayOfWeekOptions
    },
    subscriptionPackages() {
      return this.subscriptionPackageList.data
    },
    selectedSubscriptionPackage() {
      return this.form.subscription_package_id
        ? this.subscriptionPackages.find(
            (e) => e.id === this.form.subscription_package_id
          )
        : null
    },
    lessonPlans() {
      return this.lessonPlanList.data
    },
    teachers() {
      return this.isNativeTeacher
        ? this.teacherList.data.filter((e) => e.is_native_speaker)
        : this.teacherList.data
    },
    students() {
      return this.mode === 'add'
        ? this.studentList.data.filter(
            (e) =>
              e.active_classrooms.length === 0 ||
              e.active_classrooms.findIndex(
                (item) =>
                  item.subscription_package_id ===
                  this.form.subscription_package_id
              ) === -1
          )
        : this.studentList.data
    },
    isAllStudentsSelected: {
      get() {
        return (
          this.form.student_ids.length > 0 &&
          this.students.every((e) => this.form.student_ids.includes(e.id))
        )
      },
      set(value) {
        if (value) {
          this.form.student_ids = []
          this.students.forEach((e) => {
            this.form.student_ids.push(e.id)
          })
        } else {
          this.form.student_ids = []
        }
      },
    },
    isSomeStudentsSelected() {
      return (
        !this.isAllStudentsSelected &&
        this.students.some((e) => this.form.student_ids.includes(e.id))
      )
    },
    isPrivate() {
      return this.form.class_type === 'PRIVATE'
    },
    isSubscriptionPackageDisabled() {
      if (this.mode === 'add') {
        return !this.form.class_type
      }
      return false
    },
    isUpdateDailySchedulesEnabled() {
      return (
        this.form.start_date && this.form.end_date && this.isValidClassPerWeek
      )
    },
    isValidClassPerWeek() {
      return (
        this.selectedSubscriptionPackage != null &&
        this.form.weekly_schedules.filter(
          (e) => e.day_of_week != null && e.start_time && e.end_time
        ).length === this.selectedSubscriptionPackage.class_per_week
      )
    },
    lessons() {
      return this.lessonList.data
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapMutations({
      setClassroomDetail: 'classroom/setDetail',
    }),
    ...mapActions({
      getSubscriptionPackageList: 'subscriptionPackage/getList',
      getLessonPlanList: 'lessonPlan/getList',
      getTeacherList: 'teacher/getList',
      // getStudentList: 'student/getList',
      getStudentList: 'classroom/getStudentList',
      getScheduleList: 'classroom/getScheduleList',
      isScheduleListLoaing: 'classroom/isScheduleListLoading',
      getLessonList: 'lesson/getList',
      showSuccessAlert: 'showSuccessAlert',
      updateClassroom: 'classroom/update',
      generateClassSchedules: 'classroom/generateSchedules',
      extendClassSchedules: 'classroom/extendSchedules',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.classroom) {
        console.error(
          `classroom props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      await Promise.all([
        this.getTeacherList({
          status: 'ACTIVE',
        }),
        // this.getSubscriptionPackageList({
        //   status: 'ACTIVE',
        // }),
      ])
      if (this.mode != 'add' && this.classroom != null) {
        this.setClassroomDetail(this.classroom)
        _.merge(this.form, this.classroom)
        if (this.classroom.students) {
          for (const student of this.classroom.students) {
            this.form.student_ids.push(student.id)
          }
          if (this.isPrivate) {
            this.form.private_student_id = this.classroom.students[0].id
          }
        }
        if (this.classroom.subscription_package_id) {
          await Promise.all([
            // this.getStudentList({
            //   status: 'ACTIVE',
            //   subscription_package_id: this.classroom.subscription_package_id,
            //   subscription_order_status: this.mode === 'add' ? 'ACTIVE' : null,
            //   with_active_classroom: true,
            // }),
            this.getSubscriptionPackageList({
              status: 'ACTIVE',
              classroom_type: this.form.class_type,
            }),
            this.getStudentList({
              subscription_package_id: this.classroom.subscription_package_id,
            }),
            this.getLessonList({
              lesson_plan_id:
                this.classroom.subscription_package.lesson_plan_id,
            }),
          ])
        }
        const numOfDiffSession =
          this.selectedSubscriptionPackage.class_per_week -
          this.classroom.weekly_schedules.length
        if (numOfDiffSession > 0) {
          for (let i = 0; i < numOfDiffSession; i++) {
            this.addWeeklySchedule()
          }
        }
      }
    },
    subscriptionPackageItemText(item) {
      return item
        ? `${item.name} (${item.class_per_week} x ${item.minutes_per_class} mins)`
        : null
    },
    teacherItemText(item) {
      return item
        ? `${item.name} ${item.is_native_speaker ? '(Native)' : ''}`
        : null
    },
    async changeClassType(value) {
      await this.getSubscriptionPackageList({
        status: 'ACTIVE',
        classroom_type: value,
      })
      // if (this.isPrivate) {
      //   const packageIds = this.subscriptionPackages.map((e) => e.id);
      //   await this.getStudentList({
      //     subscription_package_id: packageIds
      //   })
      // }
    },
    async changePrivateStudent(value) {
      const student = this.students.find((e) => e.id === value)
      if (student) {
        const packageIds = student.active_subscriptions.map(
          (e) => e.subscription_package_id
        )
        await this.getSubscriptionPackageList({
          status: 'ACTIVE',
          classroom_type: this.form.class_type,
          id: packageIds,
        })
      }
    },
    async changeSubscriptionPackage(value) {
      const subscriptionPackage = this.subscriptionPackages.find(
        (e) => e.id === value
      )
      if (subscriptionPackage) {
        this.form.class_type = subscriptionPackage.classroom_type
        const numOfDiffSession =
          subscriptionPackage.class_per_week - this.form.weekly_schedules.length
        if (numOfDiffSession > 0) {
          for (let i = 0; i < numOfDiffSession; i++) {
            this.addWeeklySchedule()
          }
        }
        await this.getStudentList({
          subscription_package_id: subscriptionPackage.id,
          ...(this.mode === 'add' && { is_assigned: 0 }),
        })
        if (!this.isPrivate) {
          this.form.student_ids = []
          for (const student of this.students) {
            if (!this.form.student_ids.includes(student.id)) {
              this.form.student_ids.push(student.id)
            }
          }
        }
        await this.getLessonList({
          lesson_plan_id: subscriptionPackage.lesson_plan_id,
        })
        if (this.mode === 'edit') {
          // TODO: Show generate schedule dialog
        }
      }
    },
    addWeeklySchedule() {
      this.form.weekly_schedules.push({
        day_of_week: null,
        start_time: '',
        end_time: '',
      })
    },
    removeWeeklySchedule(index) {
      this.form.weekly_schedules.splice(index, 1)
    },
    onScheduleGenerated() {
      _.merge(this.form, this.classroomDetail)
    },
    async onSubmitExtendSchedule({ extendDate }) {
      await this.updateClassroom(this.classroom)
      await this.extendClassSchedules({
        id: this.form.id,
        extend_date: extendDate,
      })
      this.form.end_date = extendDate
      this.extendScheduleDialog = false
      if (this.$refs.scheduleList) {
        await this.$refs.scheduleList[0].getListData()
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    isShowTab(tab) {
      if (tab.id === 'students') {
        return !this.isPrivate
      } else if (tab.id === 'schedules') {
        return this.mode !== 'add'
      } else if (tab.id === 'studyReports') {
        return this.mode !== 'add'
      } else if (tab.id === 'holidays') {
        return this.mode !== 'add'
      }
      return true
    },
    submit() {
      if (this.$refs.form.validate()) {
        if (this.isPrivate) {
          this.form.student_ids = [this.form.private_student_id]
        }
        if (this.mode === 'add') {
          this.form.generate_schedules = true
          this.form.post('/classrooms')
        } else if (this.mode === 'edit') {
          this.form.put(`/classrooms/${this.classroom.id}`)
        }
      }
    },
  },
}
</script>
